// eslint-disable-next-line no-use-before-define
import React, { useEffect, useState } from 'react';
import { Server, apiScope } from './apis';
import type { ICompany, ICustomer } from './IDB';
import { NavbarComponent } from './components/Navbar';
import { BrowserRouter, Route, Routes, Outlet } from 'react-router';
import { CustomerList } from './pages/CustomerList';
import { CustomerInfo } from './pages/CustomerInfo';
import { LicenseBuilder } from './pages/LicenseBuilder';
import { AddCustomer } from './pages/AddCustomer';
import { ErrorPage } from './pages/ErrorPage';
import { Home } from './pages/Home';
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { Login } from './pages/Login';
import { InteractionRequiredAuthError, InteractionStatus, SilentRequest } from "@azure/msal-browser";
import { RenewLicense } from './pages/RenewLicense';

/**
 * The main license app component and top-level app logic (functional).
 */
export const LicenseApp: React.FC = () => {
    const [licenseCloudUser, setLicenseCloudUser] = useState<string>('');
    const [companies, setCompanies] = useState<ICompany[]>([]);
    const isAuthenticated = useIsAuthenticated();
    const { instance, accounts, inProgress } = useMsal();
    const [reactKey, setReactKey] = useState<number>(0);

    useEffect(() => {
        if (inProgress === InteractionStatus.None && accounts.length > 0) {
            const tokenRequest: SilentRequest = {
                account: accounts[0],
                scopes: apiScope
            }

            instance.acquireTokenSilent(tokenRequest)
                .then(async (response) => {
                    instance.setActiveAccount(instance.getAllAccounts()[0])
                    const api_server = Server.getInstance(response.accessToken);

                    const account = response.account;
                    setLicenseCloudUser(`${account.name}`);
                    
                    const upFrontCalls = [api_server.getCompanies()] as const; // Make array tuple
                    const [companyResponse] = await Promise.all(upFrontCalls);
                    setCompanies(companyResponse);
                }).catch(async (e) => {
                    // Catch interaction_required errors and call interactive method to resolve
                    if (e instanceof InteractionRequiredAuthError) {
                        await instance.acquireTokenRedirect(tokenRequest);
                    }
                    throw e;
                });
        }
    }, [inProgress, accounts, instance])


    const refreshCustomerList = async (cb?: () => void) => {
        const server = Server.getInstance();
        const companies = await server.getCompanies();
        setCompanies(companies);
        setReactKey(reactKey + 1)
        if (cb) {
            cb();
        }
    };

    if (!isAuthenticated && inProgress === "none") {
        return <Login />;
    }

    if (!licenseCloudUser) {
        return null;
    }

    const Layout = () => (
        <>
            <NavbarComponent licenseCloudUser={licenseCloudUser} />
            <Outlet />
        </>
    );

    return (
        <BrowserRouter>
            <Routes>
                <Route element={<Layout />}>
                    <Route path="/" element={<Home />} />
                    <Route path="/customers" element={<CustomerList key={reactKey} />} />
                    <Route path="/customers/:customer_id" element={<CustomerInfo key={reactKey}/>} />
                    <Route path="/licenses" element={<LicenseBuilder key={reactKey} author={licenseCloudUser} />} />
                    <Route path="/licenses/renew" element={<RenewLicense key={reactKey}/>} />
                    <Route path="/addcustomer" element={<AddCustomer key={reactKey} refresh={refreshCustomerList} companies={companies} />} />
                    <Route path="/addcustomer/:customer_id" element={<AddCustomer key={reactKey} refresh={refreshCustomerList} companies={companies} />} />
                    <Route path="/error/" element={<ErrorPage />} />
                </Route>
            </Routes>
        </BrowserRouter>
    );
};